import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Trash, Plus, Pencil, Check, X } from 'lucide-react';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const ColorManagementModal = ({ isOpen, onClose }) => {
    const { colors, addColor, deleteColor, updateColor } = usePIM();
    const { items: sortedColors, requestSort, sortConfig } = useSortableData(colors);
    const [newColorName, setNewColorName] = useState('');
    const [newColorHex, setNewColorHex] = useState('#000000');
    const [newColorPantone, setNewColorPantone] = useState('');
    const [newColorNotes, setNewColorNotes] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', hex: '', pantone: '', notes: '' });

    const handleAdd = async () => {
        if (!newColorName) return;

        await addColor({
            name: newColorName,
            hex: newColorHex,
            pantone: newColorPantone,
            notes: newColorNotes
        });

        setNewColorName('');
        setNewColorHex('#000000');
        setNewColorPantone('');
        setNewColorNotes('');
    };

    const startEdit = (color) => {
        setEditingId(color.id);
        // Use 'hex' from DB. Fallback to hex_code if necessary, but DB migration aligns this.
        setEditFormData({
            name: color.name,
            hex: color.hex || color.hex_code || '#000000',
            pantone: color.pantone || '',
            notes: color.notes || ''
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditFormData({ name: '', hex: '', pantone: '', notes: '' });
    };

    const saveEdit = async (id) => {
        await updateColor(id, editFormData);
        setEditingId(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Manage Colors" size="2xl">
            <div className="space-y-6">
                {/* Add New Color */}
                <div className="flex flex-col gap-3 bg-stone-900 p-4 rounded-lg border border-stone-700">
                    <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-4 space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Color Name</label>
                            <Input
                                value={newColorName}
                                onChange={(e) => setNewColorName(e.target.value)}
                                placeholder="e.g. Midnight Blue"
                            />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Hex Code</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={newColorHex}
                                    onChange={(e) => setNewColorHex(e.target.value)}
                                    className="h-9 w-12 rounded bg-transparent cursor-pointer border border-stone-700"
                                />
                            </div>
                        </div>
                        <div className="col-span-3 space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Pantone</label>
                            <Input
                                value={newColorPantone}
                                onChange={(e) => setNewColorPantone(e.target.value)}
                                placeholder="e.g. 19-4052"
                            />
                        </div>
                        <div className="col-span-3 space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Notes</label>
                            <Input
                                value={newColorNotes}
                                onChange={(e) => setNewColorNotes(e.target.value)}
                                placeholder="Optional notes"
                            />
                        </div>
                    </div>

                    <PrimaryButton onClick={handleAdd} className="h-9 w-full justify-center mt-2">
                        <Plus size={16} className="mr-1" /> Add Color
                    </PrimaryButton>
                </div>

                {/* Color List */}
                <div className="flex-1 overflow-hidden flex flex-col border border-stone-700 rounded-lg">
                    <div className="overflow-y-auto max-h-[400px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-stone-950 [&::-webkit-scrollbar-thumb]:bg-stone-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-stone-600">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-stone-950 text-stone-500 text-xs font-bold uppercase sticky top-0 z-10">
                                <tr>
                                    <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Hex" sortKey="hex" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Pantone" sortKey="pantone" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Notes" sortKey="notes" currentSort={sortConfig} onSort={requestSort} />
                                    <th className="px-4 py-3 border-b border-stone-800 text-right w-24">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-800 text-sm text-stone-300 bg-stone-900/50">
                                {sortedColors.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-center text-stone-500 italic">No colors defined.</td>
                                    </tr>
                                ) : (
                                    sortedColors.map((color) => (
                                        <tr key={color.id} className="hover:bg-stone-800/50 transition-colors">
                                            {editingId === color.id ? (
                                                <>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.name}
                                                            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                                            className="h-8 text-xs"
                                                            autoFocus
                                                        />
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="color"
                                                                value={editFormData.hex}
                                                                onChange={(e) => setEditFormData({ ...editFormData, hex: e.target.value })}
                                                                className="h-8 w-8 rounded bg-transparent cursor-pointer border border-stone-700"
                                                            />
                                                            <span className="text-xs font-mono text-stone-400">{editFormData.hex}</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.pantone}
                                                            onChange={(e) => setEditFormData({ ...editFormData, pantone: e.target.value })}
                                                            className="h-8 text-xs"
                                                            placeholder="Pantone"
                                                        />
                                                    </td>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.notes}
                                                            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                                                            placeholder="Notes"
                                                            className="h-8 text-xs"
                                                        />
                                                    </td>
                                                    <td className="p-2 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button
                                                                onClick={() => saveEdit(color.id)}
                                                                className="text-green-500 hover:text-green-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Check size={14} />
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                className="text-stone-500 hover:text-stone-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="p-3 font-medium text-stone-200">{color.name}</td>
                                                    <td className="p-3">
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="w-4 h-4 rounded-full border border-stone-600 shadow-sm"
                                                                style={{ backgroundColor: color.hex || color.hex_code }}
                                                            ></div>
                                                            <span className="text-xs font-mono text-stone-500">{color.hex || color.hex_code}</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-stone-400">{color.pantone || '-'}</td>
                                                    <td className="p-3 text-stone-400 max-w-[150px] truncate" title={color.notes}>{color.notes || '-'}</td>
                                                    <td className="p-3 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button
                                                                onClick={() => startEdit(color)}
                                                                className="text-stone-500 hover:text-blue-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Pencil size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => deleteColor(color.id)}
                                                                className="text-stone-500 hover:text-red-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Trash size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
